import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, message, Button, List, Tag, Select } from 'antd';
import DraggableModal from '../../components/DraggableModal';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import api from '../../api';

const SPECIAL_LAST_LOCATION_NAME = '3办公楼库房大连港专门办案场所边检处606室办公室6楼小木屋';
const compareLocationNameAsc = (a, b) => {
  const left = String(a || '').trim();
  const right = String(b || '').trim();
  const leftSpecial = left === SPECIAL_LAST_LOCATION_NAME;
  const rightSpecial = right === SPECIAL_LAST_LOCATION_NAME;
  if (leftSpecial && !rightSpecial) return 1;
  if (!leftSpecial && rightSpecial) return -1;
  const leftParts = left.split(/(\d+)/).filter(Boolean);
  const rightParts = right.split(/(\d+)/).filter(Boolean);
  const len = Math.max(leftParts.length, rightParts.length);
  for (let i = 0; i < len; i += 1) {
    const lp = leftParts[i];
    const rp = rightParts[i];
    if (lp === undefined) return -1;
    if (rp === undefined) return 1;
    const lNum = /^\d+$/.test(lp);
    const rNum = /^\d+$/.test(rp);
    if (lNum && rNum) {
      const diff = Number(lp) - Number(rp);
      if (diff !== 0) return diff;
      continue;
    }
    if (lNum && !rNum) return -1;
    if (!lNum && rNum) return 1;
    const cmp = String(lp).localeCompare(String(rp), 'zh-Hans-CN', { sensitivity: 'base' });
    if (cmp !== 0) return cmp;
  }
  return 0;
};

export default function PartitionDetails() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTitle, setDetailTitle] = useState('');
  const [detailPartitions, setDetailPartitions] = useState([]);
  const [showUnselected, setShowUnselected] = useState(false);
  const [partitionOptions, setPartitionOptions] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignTargetName, setAssignTargetName] = useState('');
  const [assignPartitionIds, setAssignPartitionIds] = useState([]);

  const fetchUsage = async () => {
    setLoading(true);
    try {
      const res = await api.get('/partitions/location-usage-by-name');
      const list = Array.isArray(res?.all) ? res.all : [];
      const sorted = [...list].sort((a, b) => compareLocationNameAsc(a.location_name, b.location_name));
      setRows(sorted);
    } catch (err) {
      message.error(err.message || '获取分区详情失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    fetchUsage();
    api.get('/partitions')
      .then((res) => {
        if (!mounted) return;
        const arr = Array.isArray(res) ? res : [];
        setPartitionOptions(arr.map((x) => ({ value: x.id, label: x.name })));
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const unselectedRows = useMemo(() => rows.filter(r => Number(r.selected_count || 0) === 0), [rows]);
  const selectedRows = useMemo(() => rows.filter(r => Number(r.selected_count || 0) > 0), [rows]);

  const openDetail = async (record) => {
    if (!record?.location_name || Number(record.selected_count || 0) <= 0) return;
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await api.post('/partitions/location-usage/by-name/partitions', {
        location_name: record.location_name,
      });
      setDetailTitle(res?.location_name || record.location_name || '');
      setDetailPartitions(Array.isArray(res?.partitions) ? res.partitions : []);
    } catch (err) {
      message.error(err.message || '获取分区明细失败');
      setDetailTitle(record.location_name || '');
      setDetailPartitions([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const openAssign = async (record) => {
    const name = record?.location_name || '';
    if (!name) return;
    setAssignTargetName(name);
    setAssignOpen(true);
    setAssignSaving(true);
    try {
      const res = await api.post('/partitions/location-usage/by-name/partitions', {
        location_name: name,
      });
      const ps = Array.isArray(res?.partitions) ? res.partitions : [];
      setAssignPartitionIds(ps.map(x => x.id));
    } catch {
      setAssignPartitionIds([]);
    } finally {
      setAssignSaving(false);
    }
  };

  const saveAssign = async () => {
    if (!assignTargetName) return;
    setAssignSaving(true);
    try {
      await api.post('/partitions/location-usage/by-name/assign', {
        location_name: assignTargetName,
        partition_ids: assignPartitionIds,
      });
      message.success('分配已保存');
      setAssignOpen(false);
      await fetchUsage();
    } catch (err) {
      message.error(err.message || '保存分配失败');
    } finally {
      setAssignSaving(false);
    }
  };

  const columns = [
    { title: '存放地点', dataIndex: 'location_name', key: 'location_name' },
    {
      title: '被选次数',
      dataIndex: 'selected_count',
      key: 'selected_count',
      width: 120,
      render: (v, record) => {
        const n = Number(v || 0);
        if (n <= 0) return 0;
        return <Button type="link" size="small" onClick={() => openDetail(record)}>{n}</Button>;
      },
    },
    {
      title: '分配',
      key: 'assign',
      width: 130,
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => openAssign(record)}>
          {Number(record?.selected_count || 0) > 0 ? '编辑分配' : '分配'}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Card
        style={{ marginBottom: 16 }}
        title={(
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Button
              type="text"
              size="small"
              icon={showUnselected ? <DownOutlined /> : <RightOutlined />}
              onClick={() => setShowUnselected(!showUnselected)}
            />
            <span>{`未被选（${unselectedRows.length}）`}</span>
          </span>
        )}
      >
        {showUnselected ? (
          <Table
            rowKey="location_name"
            columns={columns}
            dataSource={unselectedRows}
            loading={loading}
            pagination={false}
            locale={{ emptyText: '全部存放地点都已被分区选择' }}
          />
        ) : null}
      </Card>

      <Card title={`被选（${selectedRows.length}）`}>
        <Table
          rowKey="location_name"
          columns={columns}
          dataSource={selectedRows}
          loading={loading}
          pagination={{ pageSize: 20, showTotal: t => `共 ${t} 条` }}
          locale={{ emptyText: '暂无被选中的存放地点' }}
        />
      </Card>

      <DraggableModal
        title={`分区明细 - ${detailTitle || '存放地点'}`}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        destroyOnClose
      >
        <List
          loading={detailLoading}
          dataSource={detailPartitions}
          locale={{ emptyText: '该存放地点当前未被任何分区选中' }}
          renderItem={(item) => (
            <List.Item>
              <Tag color="blue">{item.name}</Tag>
            </List.Item>
          )}
        />
      </DraggableModal>

      <DraggableModal
        title={`分配分区 - ${assignTargetName || ''}`}
        open={assignOpen}
        confirmLoading={assignSaving}
        onOk={saveAssign}
        onCancel={() => setAssignOpen(false)}
        destroyOnClose
      >
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="请选择分区"
          value={assignPartitionIds}
          onChange={setAssignPartitionIds}
          options={partitionOptions}
          optionFilterProp="label"
          showSearch
        />
      </DraggableModal>
    </div>
  );
}
